import { DishItemDto, DishPriceCoverageDto } from '@/api/generated';
import {
  DishCategoryOptionsTranslation,
  dishCategoryOptionsTranslation,
} from '@/assets/locales/utils/dictionaries/dictionaries.translation.pl.ts';
import { DishAttention, PeriodState } from '@/views/dishes/editor/util/dishFormat.util.ts';
import { DishesListSearchFormModel } from '@/views/dishes/list/model/DishesListSearchForm.model.ts';

export type DishesListViewTranslation = {
  header: {
    button: {
      add: string;
    };
  };
  table: {
    body: {
      actions: string;
      activeOff: string;
      activeOn: string;
      minimumPrice: string;
      noPrice: string;
      yearRound: string;
    } & Record<keyof Omit<DishItemDto, 'dishId'>, string> & {
        dishCategoryOptions: DishCategoryOptionsTranslation;
      };
    browse: {
      button: string;
    };
    details: {
      attention: Record<DishAttention, string>;
      browseFromHere: string;
      categories: DishCategoryOptionsTranslation;
      category: string;
      description: string;
      diets: string;
      dietsAndTags: string;
      dish: string;
      edit: string;
      measurement: string;
      minimum: string;
      noDiets: string;
      noPrices: string;
      noTags: string;
      open: string;
      periods: Record<'off' | PeriodState, string>;
      prices: string;
      season: string;
      tags: string;
      yearRound: string;
    };
    head: {
      priceCoverageOptions: Record<DishPriceCoverageDto, string>;
      seasonalityPlaceholder: string;
    } & Record<keyof DishesListSearchFormModel, string>;
    priceExtend: {
      button: string;
      column: {
        currentPeriod: string;
        currentPrice: string;
        dish: string;
        newPeriod: string;
        newPrice: string;
      };
      failed: string;
      loading: string;
      noSource: string;
      raiseBy: string;
      raiseByApply: string;
      result: string;
      selectedSummary: string;
      submit: string;
      title: string;
    };
    quickFilters: {
      active: string;
      expiring: string;
      inactive: string;
      noPrice: string;
    };
  };
};

export const dishesListViewTranslation: DishesListViewTranslation = {
  header: {
    button: {
      add: 'Dodaj danie',
    },
  },
  table: {
    body: {
      actions: '',
      active: 'Status',
      activeOff: 'Wyłączone',
      activeOn: 'Aktywne',
      availability: 'Dostępność w terminie',
      category: 'Kategoria',
      department: 'Dział',
      description: 'Opis',
      dietaryPreferences: 'Preferencje',
      dishCategoryOptions: dishCategoryOptionsTranslation,
      dishPortion: 'Cena sug.',
      measurement: 'Porcja',
      minimumPrice: 'min. {{value}}',
      name: 'Nazwa',
      noPrice: 'brak',
      priceValidUntil: 'Cennik do',
      seasonality: 'Sezon',
      tags: 'Tagi',
      type: 'Rodzaj',
      yearRound: 'cały rok',
    },
    browse: {
      button: 'Przeglądaj po kolei ({{count}})',
    },
    details: {
      attention: {
        expiring: 'Cennik kończy się przed horyzontem — przedłuż go.',
        noPrice: 'Brak aktualnej ceny — w ofercie pójdzie przelicznik.',
        noTags: 'Brak tagów — danie gorzej trafia do podpowiedzi.',
      },
      browseFromHere: 'Przeglądaj od tego dania',
      categories: dishCategoryOptionsTranslation,
      category: 'Kategoria',
      description: 'Opis / dodatki w cenie',
      diets: 'Diety',
      dietsAndTags: 'Diety i tagi',
      dish: 'Danie',
      edit: 'Edytuj',
      measurement: 'Porcja',
      minimum: 'min. {{value}}',
      noDiets: 'Bez oznaczeń',
      noPrices: 'Brak cennika — w ofercie pójdzie przelicznik.',
      noTags: 'Brak tagów',
      open: 'Otwórz kartę',
      periods: {
        current: 'Obowiązuje',
        future: 'Zaplanowany',
        off: 'Wyłączony',
        past: 'Minął',
      },
      prices: 'Cennik',
      season: 'Sezon',
      tags: 'Tagi',
      yearRound: 'Cały rok',
    },
    head: {
      active: 'Aktualnie dostępne',
      category: 'Kategoria',
      department: 'Dział',
      dietaryPreferences: 'Preferencje',
      fullTextSearchInput: 'Szukaj',
      priceCoverage: 'Cennik',
      priceCoverageOptions: {
        COVERED: 'Pokryty do horyzontu',
        EXPIRING: 'Kończy się przed horyzontem',
        MISSING: 'Brak / przeterminowany',
      },
      priceHorizonMonths: 'Horyzont cennika',
      seasonalityFrom: 'Sezonowość od',
      seasonalityPlaceholder: 'MM/DD',
      seasonalityTo: 'Sezonowość do',
      tags: 'Tagi',
      type: 'Rodzaj dania',
    },
    priceExtend: {
      button: 'Przedłuż cenniki…',
      column: {
        currentPeriod: 'Obecny cennik',
        currentPrice: 'Obecne min / sug',
        dish: 'Danie',
        newPeriod: 'Nowy okres',
        newPrice: 'Nowe min / sug',
      },
      failed: 'Nie utworzono ({{count}}):',
      loading: 'Wczytuję dania z aktualnego filtra…',
      noSource: 'Brak cennika źródłowego — dodaj pierwszy cennik w karcie dania',
      raiseBy: 'Podnieś ceny o %',
      raiseByApply: 'Przelicz',
      result: 'Utworzono cenniki: {{created}} z {{total}}',
      selectedSummary: 'Zaznaczono {{count}} z {{total}} dań',
      submit: 'Wystaw cenniki',
      title: 'Przedłuż cenniki na kolejny okres',
    },
    quickFilters: {
      active: 'Aktywne',
      expiring: 'Cennik się kończy',
      inactive: 'Wyłączone',
      noPrice: 'Bez ceny',
    },
  },
};
